import { fileToBase64, getLocalFile, deleteLocalFile } from "./fileStorage.js";
import {
  getFileQueueByRoute,
  updateFileQueueStatus,
  deleteFileQueueByRoute,
} from "./fileQueue.js";
import { db } from "./index.js";
import { DebugLogger } from "./debugLogger.js";

/**
 * File Upload Module (Mobile-Only)
 *
 * Handles sanitation route file upload workflow:
 * - Route metadata and file batching
 * - Mobile-optimized upload batches (2MB)
 * - Upload progress tracking
 * - Upload-specific file cleanup
 *
 * For basic file storage operations, see fileStorage.js
 */

// Upload route with separated metadata and file processing
export async function uploadSanitationRouteWithFiles(
  routeData,
  progressCallback = null,
  existingServerRouteId = null
) {
  const {
    sanitationRoute,
    routeDatapoints,
    routeLocations,
    totalTreesInRoute,
  } = routeData;

  // Get file queue to check file count for progress reporting
  const fileQueue = await getFileQueueByRoute(sanitationRoute.id);

  // Declared outside try so the catch block can propagate it on partial failure
  let serverRouteId = existingServerRouteId;

  try {
    // Step 1: Upload route metadata (without files), in batches for large
    // routes. Always re-run on retry — the server merge is idempotent, and a
    // previous attempt may have uploaded only some of the batches.
    if (progressCallback) progressCallback("metadata", 0);

    serverRouteId =
      (await uploadRouteMetadata(
        {
          sanitationRoute,
          routeDatapoints,
          routeLocations,
          totalTreesInRoute,
        },
        progressCallback
      )) || serverRouteId;

    // Step 2: Get file mappings from server
    if (progressCallback)
      progressCallback("files", 0, { current: 0, total: fileQueue.length });

    const fileMappings = await getFileMappings(
      sanitationRoute.farm_id,
      serverRouteId
    );

    // Step 3: Upload files in batches
    await uploadRouteFiles(
      sanitationRoute.id,
      sanitationRoute.farm_id,
      serverRouteId,
      fileMappings,
      progressCallback
    );

    if (progressCallback) progressCallback("completed", 100);

    return { success: true, serverRouteId };
  } catch (error) {
    console.error("Error uploading route with files:", error);
    if (progressCallback) progressCallback("error", 0);
    // Attach serverRouteId so the caller can reuse it on retry. A mid-batch
    // metadata failure already carries the id of the created route.
    error.serverRouteId = error.serverRouteId ?? serverRouteId;
    throw error;
  }
}

const METADATA_BATCH_SIZE = 500;
const METADATA_BATCH_TIMEOUT_MS = 120000;

// Splits datapoints + locations into aligned batches of at most
// METADATA_BATCH_SIZE rows each, so 10k-sample routes never ship as one
// multi-MB request. The server merges batches idempotently (same
// completed_at → same route).
export function splitRouteDataIntoBatches(routeDatapoints, routeLocations) {
  const datapoints = routeDatapoints || [];
  const locations = routeLocations || [];
  const batchSize = METADATA_BATCH_SIZE;
  const batchCount = Math.max(
    Math.ceil(datapoints.length / batchSize),
    Math.ceil(locations.length / batchSize),
    1
  );

  return Array.from({ length: batchCount }, (_, index) => ({
    routeDatapoints: datapoints.slice(index * batchSize, (index + 1) * batchSize),
    routeLocations: locations.slice(index * batchSize, (index + 1) * batchSize),
    finalBatch: index === batchCount - 1,
  }));
}

// Upload route metadata in sequential batches. Returns the server route id.
// Throws on the first failed batch, tagging the error with the serverRouteId
// from earlier batches so the caller can persist it for retries.
export async function uploadRouteMetadata(
  { sanitationRoute, routeDatapoints, routeLocations, totalTreesInRoute },
  progressCallback = null
) {
  const batches = splitRouteDataIntoBatches(routeDatapoints, routeLocations);
  let serverRouteId = null;

  for (const [index, batch] of batches.entries()) {
    try {
      const response = await postMetadataBatch({
        sanitationRoute,
        totalTreesInRoute,
        batch,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to upload route metadata (batch ${index + 1}/${batches.length})`
        );
      }

      serverRouteId = (await extractRouteIdFromResponse(response)) ?? serverRouteId;
    } catch (error) {
      error.serverRouteId = serverRouteId;
      throw error;
    }

    if (progressCallback) {
      progressCallback(
        "metadata",
        Math.round(((index + 1) / batches.length) * 100)
      );
    }
  }

  return serverRouteId;
}

async function postMetadataBatch({ sanitationRoute, totalTreesInRoute, batch }) {
  const formData = new FormData();
  formData.append("data[sanitation_route]", JSON.stringify(sanitationRoute));
  formData.append(
    "data[route_datapoints]",
    JSON.stringify(batch.routeDatapoints)
  );
  formData.append("data[route_locations]", JSON.stringify(batch.routeLocations));
  formData.append("data[total_trees_in_route]", totalTreesInRoute);
  // The server evaluates real-time alerts only on the final batch, once the
  // whole route is assembled.
  formData.append("data[final_batch]", batch.finalBatch ? "true" : "false");

  const abortController = new AbortController();
  const timeoutId = setTimeout(
    () => abortController.abort(),
    METADATA_BATCH_TIMEOUT_MS
  );

  try {
    return await fetch(
      `/farms/${sanitationRoute.farm_id}/upload_sanitation_route`,
      {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
        signal: abortController.signal,
      }
    );
  } finally {
    clearTimeout(timeoutId);
  }
}

// Get file mappings from server to connect local files with server CustomFieldValues
export async function getFileMappings(farmId, serverRouteId) {
  const response = await fetch(
    `/farms/${farmId}/sanitation_routes/${serverRouteId}/file_mappings.json`,
    {
      headers: {
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to get file mappings");
  }

  return await response.json();
}

// Upload files for a specific route
export async function uploadRouteFiles(
  localRouteId,
  farmId,
  serverRouteId,
  fileMappings,
  progressCallback
) {
  const fileQueue = await getFileQueueByRoute(localRouteId);

  // Filter out already-completed files (safe for retries — skip what's done)
  const pendingFiles = fileQueue.filter(
    (entry) => entry.upload_status !== "completed"
  );

  if (pendingFiles.length === 0) {
    return; // All files already uploaded (or none to upload)
  }

  // Reset failed/uploading statuses to pending so they get retried
  for (const entry of pendingFiles) {
    if (
      entry.upload_status === "failed" ||
      entry.upload_status === "uploading"
    ) {
      await updateFileQueueStatus(entry.id, "pending", 0, null);
    }
  }

  // Group files by batches (mobile-optimized)
  const maxBatchSize = 2 * 1024 * 1024; // 2MB for mobile
  const batches = groupFilesBySize(pendingFiles, maxBatchSize);

  DebugLogger.info("File upload batching", {
    batchCount: batches.length,
    maxBatchSizeMB: maxBatchSize / 1024 / 1024,
    totalFiles: pendingFiles.length,
    skippedCompleted: fileQueue.length - pendingFiles.length,
  });

  let uploadedCount = 0;
  const totalFiles = pendingFiles.length;

  for (const batch of batches) {
    try {
      await uploadFileBatch(batch, farmId, serverRouteId, fileMappings);

      // Update progress
      uploadedCount += batch.length;
      const progress = Math.round((uploadedCount / totalFiles) * 100);
      if (progressCallback)
        progressCallback("files", progress, {
          current: uploadedCount,
          total: totalFiles,
        });

      // Force garbage collection between batches (mobile optimization)
      if (typeof window.gc === "function") {
        window.gc();
      }

      // Add small delay between batches to prevent memory pressure
      if (batches.length > 1) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    } catch (error) {
      DebugLogger.error("File batch upload failed", {
        error: error.message,
        batchSize: batch.length,
        batchFiles: batch.map((f) => ({
          name: f.file_name,
          size: f.file_size,
        })),
        uploadedSoFar: uploadedCount,
        totalFiles: totalFiles,
      });

      // Mark failed files in the batch
      for (const fileQueueItem of batch) {
        await updateFileQueueStatus(
          fileQueueItem.id,
          "failed",
          0,
          error.message
        );
      }
      throw error;
    }
  }
}

// Upload a batch of files
export async function uploadFileBatch(fileBatch, farmId, serverRouteId, fileMappings) {
  const filesData = [];

  for (const fileQueueItem of fileBatch) {
    try {
      // Mark as uploading
      await updateFileQueueStatus(fileQueueItem.id, "uploading", 0);

      // Get file content from local storage and convert to base64
      let fileContent;
      try {
        const file = await getLocalFile(fileQueueItem.local_path);
        if (!file) {
          throw new Error(
            `File not found in local storage: ${fileQueueItem.local_path}`
          );
        }
        fileContent = await fileToBase64(file);
      } catch (error) {
        DebugLogger.error("Failed to get file content for upload", {
          fileName: fileQueueItem.file_name,
          localPath: fileQueueItem.local_path,
          fileSize: fileQueueItem.file_size,
          error: error.message,
          storageMethod: fileQueueItem.local_path.split("://")[0],
        });
        throw new Error(`Failed to retrieve file: ${error.message}`);
      }

      // Find corresponding CustomFieldValue ID from mappings (may be null for file fields)
      const customFieldValueId = findCustomFieldValueId(
        fileQueueItem,
        fileMappings
      );

      // Ensure fileQueueItem.id is valid
      if (!fileQueueItem.id) {
        throw new Error(
          `File queue item missing ID for file: ${fileQueueItem.file_name}`
        );
      }

      filesData.push({
        file_queue_id: fileQueueItem.id,
        route_datapoint_id: fileQueueItem.route_datapoint_id, // Local ID (for reference)
        custom_field_id: fileQueueItem.custom_field_id,
        custom_field_value_id: customFieldValueId, // Server ID from mapping (may be null)
        tree_number: fileQueueItem.tree_number,
        question_type: fileQueueItem.question_type,
        datapoint_created_at: fileQueueItem.datapoint_created_at,
        file_name: fileQueueItem.file_name,
        file_type: fileQueueItem.file_type,
        file_content: fileContent,
      });
    } catch (error) {
      await updateFileQueueStatus(fileQueueItem.id, "failed", 0, error.message);
      throw error;
    }
  }

  // Upload the batch with timeout and retry logic
  const formData = new FormData();
  formData.append("files", JSON.stringify(filesData));

  // Mobile-optimized timeout
  const timeoutMs = 120000; // 2 minutes for mobile

  DebugLogger.info("Starting batch upload", {
    fileCount: filesData.length,
    timeoutSeconds: timeoutMs / 1000,
  });

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(
      `/farms/${farmId}/sanitation_routes/${serverRouteId}/upload_files`,
      {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text().catch(() => "Unknown error");
      DebugLogger.error("HTTP upload failed", {
        status: response.status,
        statusText: response.statusText,
        responseText: errorText,
        fileCount: filesData.length,
      });
      throw new Error(`File upload failed: ${response.status} - ${errorText}`);
    }

    // Parse response and update file queue statuses
    try {
      const result = await response.json();

      if (result && result.uploaded_files) {
        for (const uploadedFile of result.uploaded_files) {
          await updateFileQueueStatus(
            uploadedFile.file_queue_id,
            "completed",
            100
          );
        }
      }

      if (result && result.failed_files) {
        for (const failedFile of result.failed_files) {
          await updateFileQueueStatus(
            failedFile.file_queue_id,
            "failed",
            0,
            failedFile.error
          );
        }
      }

      return result;
    } catch (parseError) {
      DebugLogger.warn(
        "Failed to parse upload response or update file statuses",
        {
          error: parseError.message,
          fileCount: filesData.length,
        }
      );
      // Status update failure must not block the upload itself
      return { success: true, parseError: true };
    }
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === "AbortError") {
      DebugLogger.error("Upload timeout", {
        timeoutSeconds: timeoutMs / 1000,
        fileCount: filesData.length,
      });
      throw new Error(
        `Upload timeout after ${
          timeoutMs / 1000
        } seconds. Check your network connection.`
      );
    }

    if (
      error.message.includes("NetworkError") ||
      error.message.includes("fetch")
    ) {
      DebugLogger.error("Network error during upload", {
        error: error.message,
        fileCount: filesData.length,
      });
      throw new Error(
        `Network error during upload. Check your internet connection.`
      );
    }

    DebugLogger.error("Unknown upload error", {
      error: error.message,
      errorName: error.name,
      fileCount: filesData.length,
    });
    throw error;
  }
}

// Group files into batches by total size
export function groupFilesBySize(fileQueue, maxBatchSize) {
  const batches = [];
  let currentBatch = [];
  let currentBatchSize = 0;

  for (const file of fileQueue) {
    if (
      currentBatchSize + file.file_size > maxBatchSize &&
      currentBatch.length > 0
    ) {
      batches.push(currentBatch);
      currentBatch = [file];
      currentBatchSize = file.file_size;
    } else {
      currentBatch.push(file);
      currentBatchSize += file.file_size;
    }
  }

  if (currentBatch.length > 0) {
    batches.push(currentBatch);
  }

  return batches;
}

// Compare two ISO timestamps within 2-second tolerance to handle
// precision differences between JS Date.toISOString() and PostgreSQL datetime.
export function timestampsMatch(clientTs, serverTs) {
  if (!clientTs || !serverTs) return false;
  const clientMs = new Date(clientTs).getTime();
  const serverMs = new Date(serverTs).getTime();
  return Math.abs(clientMs - serverMs) < 2000;
}

// Find the CustomFieldValue ID for a file from the server mappings
export function findCustomFieldValueId(fileQueueItem, fileMappings) {
  let matchingDatapoint;

  if (fileQueueItem.question_type === "route") {
    matchingDatapoint = fileMappings.route_datapoints.find(
      (dp) => dp.datapoint_type === fileQueueItem.question_type
    );
  } else {
    // Match by tree_number + datapoint_type + created_at for uniqueness.
    // Multiple tree_samples per tree share tree_number + type, so created_at
    // disambiguates which exact datapoint this file belongs to.
    const datapointCreatedAt = fileQueueItem.datapoint_created_at;

    if (datapointCreatedAt) {
      matchingDatapoint = fileMappings.route_datapoints.find(
        (dp) =>
          dp.tree_number === fileQueueItem.tree_number &&
          dp.datapoint_type === fileQueueItem.question_type &&
          timestampsMatch(datapointCreatedAt, dp.created_at)
      );
    }

    // Fallback for older file queue entries without datapoint_created_at
    if (!matchingDatapoint) {
      matchingDatapoint = fileMappings.route_datapoints.find(
        (dp) =>
          dp.tree_number === fileQueueItem.tree_number &&
          dp.datapoint_type === fileQueueItem.question_type
      );
    }
  }

  if (!matchingDatapoint) {
    return null;
  }

  const matchingCFV = matchingDatapoint.custom_field_values.find(
    (cfv) => cfv.custom_field_id === fileQueueItem.custom_field_id
  );

  return matchingCFV ? matchingCFV.id : null;
}

// Extract route ID from upload response
async function extractRouteIdFromResponse(response) {
  const responseData = await response.json();
  return responseData.sanitation_route_id;
}

// Clean up uploaded files from local storage after successful upload
export async function cleanupUploadedFiles(localRouteId) {
  try {
    console.log(`Starting cleanup for route ${localRouteId}`);

    // Get all file queue entries for this route before deletion
    const fileQueueEntries = await getFileQueueByRoute(localRouteId);

    let deletedFiles = 0;
    let errors = [];

    // Delete actual file blobs from storage
    for (const entry of fileQueueEntries) {
      if (entry.local_path) {
        try {
          const success = await deleteLocalFile(entry.local_path);
          if (success) {
            deletedFiles++;
          } else {
            errors.push(`Failed to delete file: ${entry.file_name}`);
          }
        } catch (error) {
          console.error(`Error deleting file ${entry.file_name}:`, error);
          errors.push(`Error deleting ${entry.file_name}: ${error.message}`);
        }
      }
    }

    // Delete file queue entries
    await deleteFileQueueByRoute(localRouteId);

    console.log(
      `Cleanup completed for route ${localRouteId}: ${deletedFiles} files deleted`
    );

    if (errors.length > 0) {
      console.warn(`Cleanup warnings for route ${localRouteId}:`, errors);
    }

    return {
      success: true,
      deletedFiles,
      errors,
    };
  } catch (error) {
    console.error(`Cleanup failed for route ${localRouteId}:`, error);
    return {
      success: false,
      error: error.message,
      deletedFiles: 0,
      errors: [error.message],
    };
  }
}

// Clean up failed uploads and orphaned files
export async function cleanupFailedFiles(localRouteId) {
  try {
    console.log(`Cleaning up failed files for route ${localRouteId}`);

    // Get all failed file queue entries
    const allEntries = await db.fileQueue
      .where("sanitation_route_id")
      .equals(localRouteId)
      .toArray();

    const failedEntries = allEntries.filter(
      (entry) =>
        entry.upload_status === "failed" || entry.upload_status === "pending"
    );

    let cleanedCount = 0;
    let errors = [];

    for (const entry of failedEntries) {
      try {
        // Delete the file blob if it exists
        if (entry.local_path) {
          await deleteLocalFile(entry.local_path);
        }

        // Delete the file queue entry
        await db.fileQueue.delete(entry.id);
        cleanedCount++;
      } catch (error) {
        console.error(`Error cleaning failed file ${entry.file_name}:`, error);
        errors.push(`${entry.file_name}: ${error.message}`);
      }
    }

    console.log(
      `Cleaned up ${cleanedCount} failed files for route ${localRouteId}`
    );

    return {
      success: true,
      cleanedCount,
      errors,
    };
  } catch (error) {
    console.error(
      `Failed file cleanup error for route ${localRouteId}:`,
      error
    );
    return {
      success: false,
      error: error.message,
      cleanedCount: 0,
      errors: [error.message],
    };
  }
}

// Clean up all orphaned files (files without corresponding queue entries)
export async function cleanupOrphanedFiles() {
  try {
    console.log("Starting orphaned files cleanup");

    // Get all file blobs
    const allFileBlobs = await db.fileBlobs.toArray();

    // Get all file queue entries
    const allQueueEntries = await db.fileQueue.toArray();
    const queuedFileIds = new Set(
      allQueueEntries.map((entry) => entry.file_local_id)
    );

    // Find orphaned files (blobs without queue entries)
    const orphanedFiles = allFileBlobs.filter(
      (blob) => !queuedFileIds.has(blob.file_id)
    );

    let deletedCount = 0;
    let errors = [];

    for (const orphanedFile of orphanedFiles) {
      try {
        // Check if file is old enough to delete (older than 24 hours)
        const fileAge = new Date() - new Date(orphanedFile.created_at);
        const hoursSinceCreation = fileAge / (1000 * 60 * 60);

        if (hoursSinceCreation > 24) {
          const localPath = `indexeddb://${orphanedFile.file_id}`;

          await deleteLocalFile(localPath);
          deletedCount++;
          console.log(`Deleted orphaned file: ${orphanedFile.file_name}`);
        }
      } catch (error) {
        console.error(
          `Error deleting orphaned file ${orphanedFile.file_name}:`,
          error
        );
        errors.push(`${orphanedFile.file_name}: ${error.message}`);
      }
    }

    console.log(
      `Orphaned file cleanup completed: ${deletedCount} files deleted`
    );

    return {
      success: true,
      deletedCount,
      errors,
    };
  } catch (error) {
    console.error("Orphaned file cleanup failed:", error);
    return {
      success: false,
      error: error.message,
      deletedCount: 0,
      errors: [error.message],
    };
  }
}
